'use client';

import {
	mergeLiveRow,
	OtnChannels,
	OtnSocketMessageData,
	readGameRow,
	readPeriodRow,
	readTeamRow,
} from '@/helpers/sockets';
import { OtnGame } from '@models/ot_nationals/otn_game';
import { OtnGameOtnTeam } from '@models/ot_nationals/otn_game_otn_team';
import { useWebSocketContext } from '../contexts/WebsocketContext';

import { OtnGameOtnTeamPeriod } from '@models/ot_nationals/otn_game_otn_team_period';
import { useEffect, useState } from 'react';

type GamePeriodsState = {
	homePeriods: OtnGameOtnTeamPeriod[];
	awayPeriods: OtnGameOtnTeamPeriod[];
};

// New: what the hook returns to callers
type GameTeamPeriodsResult = {
	homeTeam: OtnGameOtnTeam | undefined;
	awayTeam: OtnGameOtnTeam | undefined;
	homePeriods: OtnGameOtnTeamPeriod[];
	awayPeriods: OtnGameOtnTeamPeriod[];
};

const upsertPeriod = (list: OtnGameOtnTeamPeriod[], period: OtnGameOtnTeamPeriod): OtnGameOtnTeamPeriod[] => {
	const idx = list.findIndex((p) => p.id === period.id);

	// New period row → append
	if (idx === -1) {
		return [...list, period];
	}

	// Existing period row → patch it
	const copy = [...list];
	copy[idx] = { ...copy[idx], ...period };
	return copy;
};

export const useGameTeamPeriodsLiveScores = (game: OtnGame): GameTeamPeriodsResult => {
	const { subscribeToChannel } = useWebSocketContext();

	const gameId = game?.id;

	// Derive home/away game-team rows from the game (DB snapshot)
	const homeGameTeam = game?.otn_games_otn_teams?.find((team) => team.is_home_team);
	const awayGameTeam = game?.otn_games_otn_teams?.find((team) => !team.is_home_team);

	const homeGameTeamId = homeGameTeam?.id;
	const awayGameTeamId = awayGameTeam?.id;

	// Seed periods from DB snapshot
	const initialHomePeriods = (homeGameTeam?.otn_game_otn_team_periods ?? []) as OtnGameOtnTeamPeriod[];
	const initialAwayPeriods = (awayGameTeam?.otn_game_otn_team_periods ?? []) as OtnGameOtnTeamPeriod[];

	const [state, setState] = useState<GamePeriodsState>({
		homePeriods: initialHomePeriods,
		awayPeriods: initialAwayPeriods,
	});

	// Resync when we navigate to a different game / or DB snapshot changes
	useEffect(() => {
		setState({
			homePeriods: (homeGameTeam?.otn_game_otn_team_periods ?? []) as OtnGameOtnTeamPeriod[],
			awayPeriods: (awayGameTeam?.otn_game_otn_team_periods ?? []) as OtnGameOtnTeamPeriod[],
		});
	}, [gameId, homeGameTeamId, awayGameTeamId]);

	useEffect(() => {
		if (!gameId) return;

		const unsubscribe = subscribeToChannel(OtnChannels.TEAMS_PERIODS, gameId, (data: OtnSocketMessageData) => {
			const livePeriod = readPeriodRow(data);
			if (!livePeriod) return;

			const gameTeamId = livePeriod.otn_game_otn_team_id;

			// Applied immediately: a debounce here kept only the last update in a burst, so a
			// quarter score for the other team or another period could be dropped.
			setState((prev) => {
				if (gameTeamId === homeGameTeamId) {
					return {
						...prev,
						homePeriods: upsertPeriod(prev.homePeriods, livePeriod),
					};
				}

				if (gameTeamId === awayGameTeamId) {
					return {
						...prev,
						awayPeriods: upsertPeriod(prev.awayPeriods, livePeriod),
					};
				}

				return prev;
			});
		});

		return unsubscribe;
	}, [gameId, homeGameTeamId, awayGameTeamId, subscribeToChannel]);

	// New: expose both the teams and the periods
	return {
		homeTeam: homeGameTeam,
		awayTeam: awayGameTeam,
		homePeriods: state.homePeriods,
		awayPeriods: state.awayPeriods,
	};
};

type GameStatusAndScoresData = {
	game: OtnGame;
	gameTeamHomeStats: OtnGameOtnTeam | null;
	gameTeamAwayStats: OtnGameOtnTeam | null;
};

export const useGameStatusAndScoresUpdates = (initialGame: OtnGame) => {
	const { subscribeToChannel } = useWebSocketContext();

	const initialGameTeamHomeStats: OtnGameOtnTeam | undefined = initialGame.otn_games_otn_teams?.find(
		(team) => team.is_home_team,
	);
	const initialGameTeamAwayStats: OtnGameOtnTeam | undefined = initialGame.otn_games_otn_teams?.find(
		(team) => !team.is_home_team,
	);

	const gameId = initialGame?.id;
	const homeTeamId = initialGameTeamHomeStats?.otn_team_id;
	const awayTeamId = initialGameTeamAwayStats?.otn_team_id;

	// Seed from DB
	const [gameTeamsData, setGameTeamsData] = useState<GameStatusAndScoresData>({
		game: initialGame,
		gameTeamHomeStats: initialGameTeamHomeStats ?? null,
		gameTeamAwayStats: initialGameTeamAwayStats ?? null,
	});

	useEffect(() => {
		setGameTeamsData({
			game: initialGame,
			gameTeamHomeStats: initialGameTeamHomeStats ?? null,
			gameTeamAwayStats: initialGameTeamAwayStats ?? null,
		});
	}, [initialGame.id, initialGameTeamHomeStats?.otn_team_id, initialGameTeamAwayStats?.otn_team_id]);

	useEffect(() => {
		if (!gameId) return;

		const unsubscribes: Array<() => void> = [];
		unsubscribes.push(
			subscribeToChannel(OtnChannels.GAMES, gameId, (data: OtnSocketMessageData) => {
				const liveGame = readGameRow(data, gameId);
				if (!liveGame) return;
				setGameTeamsData((prev) => ({ ...prev, game: mergeLiveRow(prev.game, liveGame) }));
			}),
		);
		unsubscribes.push(
			subscribeToChannel(OtnChannels.TEAMS, gameId, (data: OtnSocketMessageData) => {
				const liveTeam = readTeamRow(data, gameId);
				if (!liveTeam) return;

				// Merge rather than replace: the socket row has no otn_team, and replacing it would
				// blank the team name, logo and link on the scoreboard.
				if (liveTeam.otn_team_id === awayTeamId) {
					setGameTeamsData((prev) => ({
						...prev,
						gameTeamAwayStats: prev.gameTeamAwayStats ? mergeLiveRow(prev.gameTeamAwayStats, liveTeam) : liveTeam,
					}));
				} else if (liveTeam.otn_team_id === homeTeamId) {
					setGameTeamsData((prev) => ({
						...prev,
						gameTeamHomeStats: prev.gameTeamHomeStats ? mergeLiveRow(prev.gameTeamHomeStats, liveTeam) : liveTeam,
					}));
				}
			}),
		);

		return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
	}, [gameId, awayTeamId, homeTeamId, subscribeToChannel]);

	return gameTeamsData;
};
